/**
 * ⚠️  WIP — NOT WIRED UP, DOES NOT WORK CORRECTLY YET.
 *
 * Exploration of pure client-side filter / sort / paginate for the Style
 * Matches PLP: the Peacock middleware would return the customer's matched
 * products once, and subsequent UI interactions (filter toggle, sort change,
 * page change) would be deterministic transforms of that cached list with
 * no extra API calls.
 *
 * Known issues that prevent shipping this:
 * - Fredhopper sluggifies facet option ids inconsistently per facet
 *   ("boho bird" → "bohobird", "Smart Casual" → "smart_casual"), so URL
 *   values never match product attribute values directly. Worked around
 *   here via a label-lookup map, but coverage is incomplete.
 * - The `categories` facet's option values are Shopify collection ids that
 *   don't appear on per-product Peacock responses, so the filter can't be
 *   resolved client-side at all.
 * - `colour_group` lives in nested `swatch_info[]`, which contains sibling
 *   colour variants — needs filtering to the current product to avoid
 *   overcounting.
 * - Result counts diverge from the server-side Fredhopper response in
 *   several cases (notably colour group), so users see different totals
 *   from what a regular Fredhopper search would return.
 *
 * Currently nothing imports this module — `BirdsnestDemoPage` routes Style
 * Matches through the normal `FredhopperSearchProvider.search()` path and
 * the middleware applies filter/sort/pagination server-side. Kept on disk
 * as a starting point if we revisit the client-side approach later.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Sentinel value for `data-collection-id` on the `#search-app` element when
 * the Style Matches Peacock middleware should be used instead of the regular
 * Fredhopper category endpoint. Mirrors the constant in `api.ts`; if you
 * change one, change the other.
 */
export const STYLE_MATCHES_COLLECTION_ID = 'style-matches';
/**
 * Internal id of the CATEGORIES facet (appears in the URL as
 * `mrf_categories=...`). Hidden on Style Matches because its option values
 * are Shopify collection memberships that the per-product Peacock response
 * does not include — there's no way to resolve the filter client-side.
 */
export const CATEGORIES_FILTER_ID = 'categories';
const norm = (s) => String(s).trim().toLowerCase();
/**
 * Resolve the values to match against on a product for a given facet id.
 *
 * Most Fredhopper facets land in `customAttributes[filterId]` (always an
 * array in practice), but a few common filters live as top-level fields on
 * the `Product` shape — `brand`, `color`, `availability`, `productType`,
 * `vendor`, `tags`. Sizes come from `product.size` plus available variants'
 * selectedOptions. `colour_group` is nested inside `swatch_info[]`.
 */
function resolveFilterValues(p, filterId) {
    var _a, _b, _c, _d, _e;
    switch (filterId) {
        case 'brand':
        case 'brand_name':
        case 'vendor': {
            const out = [];
            if (p.brand)
                out.push(p.brand);
            if (p.vendor && p.vendor !== p.brand)
                out.push(p.vendor);
            return out;
        }
        case 'color':
        case 'colour':
            return p.color ? [p.color] : [];
        case 'product_type':
            return p.productType ? [p.productType] : [];
        case 'availability':
            return p.availability ? [p.availability] : [];
        case 'tags':
            return (_a = p.tags) !== null && _a !== void 0 ? _a : [];
        case 'size':
        case 'available_sizes': {
            const fromVariants = ((_b = p.variants) !== null && _b !== void 0 ? _b : [])
                .filter(v => v.available)
                .flatMap(v => {
                var _a;
                return ((_a = v.selectedOptions) !== null && _a !== void 0 ? _a : [])
                    .filter(o => { var _a; return ((_a = o.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'size'; })
                    .map(o => o.value);
            });
            return Array.from(new Set([...((_c = p.size) !== null && _c !== void 0 ? _c : []), ...fromVariants]));
        }
        case 'colour_group':
        case 'custom_colour_group': {
            // `swatch_info` contains one entry per *related* colour variant
            // (sibling products in the same style), so we filter to entries that
            // belong to THIS product — otherwise a Pink product with a Black
            // sibling would erroneously match a "Black" filter.
            const swatchInfo = (_d = p.customAttributes) === null || _d === void 0 ? void 0 : _d.swatch_info;
            if (!Array.isArray(swatchInfo))
                return [];
            return Array.from(new Set(swatchInfo
                .filter(sw => (sw === null || sw === void 0 ? void 0 : sw.product_id) === p.id)
                .map(sw => sw === null || sw === void 0 ? void 0 : sw.colour_group)
                .filter((g) => typeof g === 'string' && g.length > 0)));
        }
    }
    const attr = (_e = p.customAttributes) === null || _e === void 0 ? void 0 : _e[filterId];
    if (attr == null)
        return [];
    if (Array.isArray(attr))
        return attr.map(String);
    return [String(attr)];
}
function isPriceFilter(filterId) {
    return filterId === 'price' || filterId.startsWith('price_');
}
/** Effective price: sale price if set, otherwise regular. */
function priceOf(p) {
    var _a;
    return (_a = p.price.sale) !== null && _a !== void 0 ? _a : p.price.regular;
}
function applyPriceRange(products, rangeValue) {
    // Stored as `"min,max"` or `"min-max"`.
    const [minStr, maxStr] = String(rangeValue).split(/[,-]/);
    const min = Number(minStr);
    const max = Number(maxStr);
    if (!Number.isFinite(min) || !Number.isFinite(max))
        return products;
    return products.filter(p => {
        const price = priceOf(p);
        return price >= min && price <= max;
    });
}
/**
 * Build `Map<filterId, Map<optionId, optionLabel>>`.
 *
 * Fredhopper sluggifies option ids differently per facet (`"boho bird"` →
 * `bohobird`, `"Smart Casual"` → `smart_casual`), so direct equality between
 * URL value and product attribute never works. Looking up the option label
 * from the facet definition gives us the human-readable form that the
 * product attribute carries.
 */
function buildLabelLookup(filters) {
    const out = new Map();
    for (const f of filters) {
        if (!f.options)
            continue;
        const m = new Map();
        for (const opt of f.options)
            m.set(opt.id, opt.label);
        out.set(f.id, m);
    }
    return out;
}
function compareSort(sort) {
    switch (sort) {
        case 'price-asc':
            return (a, b) => priceOf(a) - priceOf(b);
        case 'price-desc':
            return (a, b) => priceOf(b) - priceOf(a);
        case 'title-asc':
            return (a, b) => a.title.localeCompare(b.title);
        case 'title-desc':
            return (a, b) => b.title.localeCompare(a.title);
        default:
            return null; // 'relevance' or undefined: preserve API order.
    }
}
/**
 * Filter / sort / paginate the Style Matches product list. Pure: returns
 * `{ products, meta }` for the consumer to apply to state.
 */
export function applyClientFilterSort(args) {
    const { baseProducts, filters, selectedFilters, sort, page, pageSize, styleMatchesPercentComplete } = args;
    const labelByOption = buildLabelLookup(filters);
    let result = [...baseProducts];
    for (const [filterId, selectedValues] of Object.entries(selectedFilters)) {
        if (!selectedValues || selectedValues.length === 0)
            continue;
        if (isPriceFilter(filterId)) {
            result = applyPriceRange(result, selectedValues[0]);
            continue;
        }
        // Translate each selected option id to its label, then intersect
        // (case-insensitively) with the product's resolved values.
        const labels = labelByOption.get(filterId);
        const selectedNorm = selectedValues.map(v => { var _a; return norm((_a = labels === null || labels === void 0 ? void 0 : labels.get(v)) !== null && _a !== void 0 ? _a : v); });
        result = result.filter(p => {
            const productValuesNorm = resolveFilterValues(p, filterId).map(norm);
            if (productValuesNorm.length === 0)
                return false;
            return selectedNorm.some(s => productValuesNorm.includes(s));
        });
    }
    const cmp = compareSort(sort);
    if (cmp)
        result.sort(cmp);
    const totalResults = result.length;
    const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const startIdx = (currentPage - 1) * pageSize;
    return {
        products: result.slice(startIdx, startIdx + pageSize),
        meta: Object.assign({ totalResults,
            totalPages,
            currentPage,
            pageSize }, (styleMatchesPercentComplete !== undefined ? { styleMatchesPercentComplete } : {})),
    };
}
let cache = null;
/**
 * The first invocation hits the Peacock middleware via `provider.search()`
 * and caches the response. Subsequent invocations skip the API entirely and
 * run the cached base list through `applyClientFilterSort` with the
 * caller-supplied filter / sort / page selection.
 *
 * Return shape mirrors what the page needs from `provider.getProducts()`,
 * `getFilters()`, `getMeta()`, `getSortOptions()` after a normal Fredhopper
 * search — so `runSearch` can dispatch on `isStyleMatches` once and feed the
 * same `setProducts` / `setFilters` / `setMeta` / `setSortOptions` calls
 * either way.
 */
export function styleMatchesSearch(args) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const { provider, query, options } = args;
        if (cache === null) {
            // `collectionId` is what `api.ts` switches on to route to the Peacock
            // middleware — pin it here so callers don't have to remember.
            yield provider.search(query, Object.assign(Object.assign({}, options), { collectionId: STYLE_MATCHES_COLLECTION_ID }));
            cache = {
                baseProducts: provider.getProducts(),
                // CATEGORIES facet is stripped here so neither the drawer nor the
                // client-side filter ever sees it — see CATEGORIES_FILTER_ID.
                baseFilters: provider.getFilters().filter(f => f.id !== CATEGORIES_FILTER_ID),
                baseSortOptions: provider.getSortOptions(),
                styleMatchesPercentComplete: (_a = provider.getMeta()) === null || _a === void 0 ? void 0 : _a.styleMatchesPercentComplete,
            };
        }
        const { products, meta } = applyClientFilterSort({
            baseProducts: cache.baseProducts,
            filters: cache.baseFilters,
            selectedFilters: (_b = options.filters) !== null && _b !== void 0 ? _b : {},
            sort: options.sort,
            page: (_c = options.page) !== null && _c !== void 0 ? _c : 1,
            pageSize: options.pageSize,
            styleMatchesPercentComplete: cache.styleMatchesPercentComplete,
        });
        return {
            products,
            filters: cache.baseFilters,
            sortOptions: cache.baseSortOptions,
            meta,
        };
    });
}
/** Drops the cached Peacock response. Tests / hot-reload only. */
export function resetStyleMatchesCache() {
    cache = null;
}
